import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import Pubnub, { type ListenerParameters } from 'pubnub'
import { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useTabStore } from 'v2/state/tab'
import { type PubNubContextType, type PubNubRef, PubnubStatus } from 'v2/types/pubnub'
import { pubnubLogger } from 'v2/utils/logger'
import { executionService } from '../services/execution-service'
import {
  EMBED_SESSION_EXPIRED_EVENT,
  EMBED_SESSION_READY_EVENT,
  getEmbedSession,
  isEmbedMode,
} from '../utils/embed-session'
import { addPubNubEventListeners } from '../utils/pubnub.util'
import { consolePubnubIdentity, embedPubnubIdentity } from '../utils/pubnub-identity'
import { getSettingsQueryKeys } from '../utils/query-key-management'
import { useAuth } from './use-auth'
import { usePubnubMutations } from './use-pubnub.mutations'

const PubNubContext = createContext<PubNubContextType | null>(null)

const pubnubConfig = {
  keepAlive: true,
  heartbeatInterval: 60,
  retryConfiguration: {
    maximumRetry: 6,
    delay: 10000,
  },
  restore: true,
}

// The provider mounts above the embed bootstrap: READY re-reads the session, and EXPIRED drops
// it, which tears PubNub down the way a console logout does.
const useEmbedSessionState = (embed: boolean) => {
  const [session, setSession] = useState(() => (embed ? getEmbedSession() : null))
  useEffect(() => {
    if (!embed) return
    const onReady = () => setSession(getEmbedSession())
    const onExpired = () => setSession(null)
    window.addEventListener(EMBED_SESSION_READY_EVENT, onReady)
    window.addEventListener(EMBED_SESSION_EXPIRED_EVENT, onExpired)
    return () => {
      window.removeEventListener(EMBED_SESSION_READY_EVENT, onReady)
      window.removeEventListener(EMBED_SESSION_EXPIRED_EVENT, onExpired)
    }
  }, [embed])
  return session
}

export const PubNubProvider = memo(
  ({ children }: { children: React.ReactNode }) => {
    const { data: profile } = useProfileQuery()
    const { userAuthenticated } = useAuth()
    // Read once: the mode belongs to the document, so a later path never moves a console provider.
    const [isEmbed] = useState(isEmbedMode)
    const embedSession = useEmbedSessionState(isEmbed)
    const { authed, publishKey, subscribeKey, userId, tokenKey, channels } = isEmbed
      ? embedPubnubIdentity(embedSession)
      : consolePubnubIdentity(profile, userAuthenticated)
    const pubnubRef = useRef<PubNubRef | null>(null)
    const listenerRef = useRef<ListenerParameters | null>(null)
    const setPubnubStatus = useTabStore((state) => state.setPubnubStatus)
    const [tokenExpiry, setTokenExpiry] = useState<number>(0) // minutes
    const pubnubActions = usePubnubMutations({ pubnubRef })

    // Register PubNub
    useEffect(() => {
      // only initialize if the pubnub object is not already initialized
      if (authed && publishKey && subscribeKey && !pubnubRef.current?.isRegistered) {
        pubnubLogger.log('PubNub initializing')
        pubnubRef.current = {
          pubnub: new Pubnub({
            publishKey,
            subscribeKey,
            userId,
            ...pubnubConfig,
          }),
          isRegistered: true,
        }
        window.cx.pubnubInstance = pubnubRef.current.pubnub
      }
      // Unregister PubNub if user is not authenticated (logged out) and if it is already registered
      if (!authed && pubnubRef.current?.isRegistered) {
        pubnubLogger.log('PubNub unregistering')
        pubnubRef.current.pubnub.setToken('')
        try {
          pubnubRef.current.pubnub.unsubscribeAll()
          if (listenerRef.current) {
            pubnubLogger.debug('Removing pubnub listeners')
            pubnubRef.current.pubnub.removeListener(listenerRef.current)
            listenerRef.current = null
          }
          pubnubRef.current.pubnub.stop?.()
        } catch (error) {
          pubnubLogger.error('Error unregistering PubNub', error)
        }
        setPubnubStatus(PubnubStatus.DISCONNECTED)
        // Clear global instance to avoid stale refs elsewhere
        window.cx.pubnubInstance = undefined
        pubnubRef.current.isRegistered = false
      }
    }, [authed, publishKey, subscribeKey, userId])

    // Add/Remove PubNub listeners
    useEffect(() => {
      if (pubnubRef.current?.pubnub && authed) {
        addPubNubEventListeners(listenerRef, pubnubRef)
      }
      const removeEventListeners = () => {
        if (listenerRef.current) {
          pubnubLogger.debug('Removing pubnub listeners')
          pubnubRef.current?.pubnub?.removeListener(listenerRef.current)
          listenerRef.current = null
        }
      }
      return removeEventListeners
    }, [pubnubRef.current?.isRegistered, authed])

    const { isError, isFetching } = useQuery({
      queryKey: getSettingsQueryKeys().pubnubToken(tokenKey),
      enabled: !!userId && authed,
      staleTime: convertToMs(tokenExpiry, TimeUnits.Minutes), // it becomes stale at ttl
      refetchInterval: convertToMs((tokenExpiry || 10) * 0.8, TimeUnits.Minutes), // refetch 20% before expiry
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      meta: { silent: true },
      queryFn: async () => {
        try {
          // Store timetoken
          pubnubLogger.log('Generating token')
          // In embed hodor ignores this body and mints for the session's own org.
          const response = await executionService.generateToken(userId)
          const parsedToken = pubnubRef.current?.pubnub.parseToken(response.data)
          pubnubLogger.log('Token generated - token expiry in ', parsedToken?.ttl, 'minutes')
          setTokenExpiry(parsedToken?.ttl || 0)
          pubnubLogger.log('PubNub setting token')
          pubnubRef.current?.pubnub.setToken(response.data)
          pubnubActions.subscribeMutation.mutate({ channels })

          setPubnubStatus(PubnubStatus.CONNECTED)
          pubnubLogger.local('PubNub connected')
          return response.data ?? ''
        } catch (error) {
          pubnubLogger.error('Error generating token', error)
          throw error
        }
      },
    })

    useEffect(() => {
      if (isError && !isFetching) setPubnubStatus(PubnubStatus.DISCONNECTED)
      else if (isFetching) setPubnubStatus(PubnubStatus.CONNECTING)
    }, [isError, setPubnubStatus, isFetching])

    const value = useMemo(() => ({ ...pubnubActions, pubnubRef }), [pubnubActions])

    return <PubNubContext.Provider value={value}>{children}</PubNubContext.Provider>
  },
  () => true,
)

export function usePubNub() {
  const pubNub = useContext(PubNubContext)
  if (!pubNub) {
    throw new Error('usePubNub must be used within a PubNubProvider')
  }
  return pubNub
}
